import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  allSteps,
  addStep,
  deleteStep,
  duplicateStep,
  changeStepOrder,
  removeLayerConfig
} from '../../../../redux/stepsSlice';
import { setActiveStep, getActiveStep, removeActiveLayer } from '../../../../redux/toolsSlice';

// Walkthrough Editor v2 — bottom step filmstrip (BL-138).
// Replaces the left StepSidebar: horizontal step thumbnails with select,
// duplicate, delete, drag-reorder, and add. Reuses the exact same Redux
// actions the legacy StepSidebar used, so behaviour + autosave are unchanged.

const ItemTypes = { STEP: 'step' };

const CopyIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);

const FilmstripStep = ({ step, index, active, onSelect, onDuplicate, onDelete, moveStep }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.STEP,
    item: { index },
    collect: (monitor) => ({ isDragging: monitor.isDragging() })
  });
  const [, drop] = useDrop({
    accept: ItemTypes.STEP,
    drop: (item) => {
      if (item.index === index) return;
      moveStep(item.index, index);
      item.index = index;
    }
  });
  const setRef = (node) => { if (node) drag(drop(node)); };

  const caption = step.title || step.name || `Step ${index + 1}`;
  const stop = (e) => e.stopPropagation();

  return (
    <div
      ref={setRef}
      className={`un-we-fstep${active ? ' on' : ''}${isDragging ? ' dragging' : ''}`}
      onClick={() => onSelect(step)}
      title={caption}
    >
      <div className="un-we-fstep__thumb">
        {step.backgroundUrl
          ? <img src={step.backgroundUrl} alt="" draggable={false} />
          : <span className="un-we-fstep__empty">No screenshot</span>}
        <div className="un-we-fstep__ctrls" onClick={stop}>
          <button type="button" title="Duplicate step" aria-label="Duplicate step" onClick={() => onDuplicate(step.id)}><CopyIcon /></button>
          <button type="button" title="Delete step" aria-label="Delete step" onClick={() => onDelete(step.id)}><TrashIcon /></button>
        </div>
      </div>
      <div className="un-we-fstep__foot">
        <span className="un-we-fstep__n">{index + 1}</span>
        <span className="un-we-fstep__t">{caption}</span>
      </div>
    </div>
  );
};

const WalkthroughFilmstrip = () => {
  const dispatch = useDispatch();
  const steps = useSelector((state) => allSteps(state)) || [];
  const activeStep = useSelector((state) => getActiveStep(state));
  const materialType = window?.location?.pathname?.split('/')?.[2];

  const onSelect = (step) => {
    dispatch(setActiveStep(step));
    dispatch(removeActiveLayer());
    dispatch(removeLayerConfig());
  };
  const onAdd = () => dispatch(addStep({ material_type: materialType }));
  const onDuplicate = (id) => dispatch(duplicateStep(id));
  const onDelete = (id) => {
    dispatch(deleteStep(id));
    if (steps.length > 1) {
      const next = steps.find((s) => s.id !== id);
      if (next) dispatch(setActiveStep(next));
    }
  };
  const moveStep = (fromIndex, toIndex) => dispatch(changeStepOrder({ fromIndex, toIndex }));

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="un-we-film">
        <span className="un-we-film__lbl">Steps</span>
        <div className="un-we-film__track">
          {steps.map((step, i) => (
            <FilmstripStep
              key={step.id}
              step={step}
              index={i}
              active={activeStep?.id === step.id}
              onSelect={onSelect}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              moveStep={moveStep}
            />
          ))}
          <button type="button" className="un-we-fadd" onClick={onAdd}>
            <span className="un-we-fadd__plus">+</span> Add step
          </button>
        </div>
      </div>
    </DndProvider>
  );
};

export default WalkthroughFilmstrip;
